<template>
  <div class="mb-4 mt-6 rounded-md bg-white p-4">
    <div class="mb-4 grid grid-cols-3 items-start gap-4 border-b pb-4">
      <InputField name="name" :label="t('name')" />
      <SelectField name="type" :label="t('documentType')" :options="documentTypes" />
      <FileInputField name="file" :label="t('upload')" />
    </div>
    <div class="flex justify-end gap-2">
      <CVButton
        variant="default"
        :is-loading="isUploadingUserDocument"
        @click.prevent="saveDocument"
      >
        {{ t('saveDocument') }}
      </CVButton>
      <CVButton :disabled="isUploadingUserDocument" @click.prevent="$emit('complete')">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CVButton from '@/components/buttons/CVButton.vue';
import FileInputField from '@/components/FileInputField.vue';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import type { User } from '@/entities/auth/user.entity';
import { useUploadAndCreateUserDocument } from '@/hooks/use-upload-and-create-user-document';
import { UserDocumentType, type UserDocumentDto } from '@carvia/ros-client-types';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm } from 'vee-validate';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

const props = defineProps<{
  user: User;
  userDocuments: UserDocumentDto[];
}>();

const emit = defineEmits<{
  (e: 'complete'): void;
}>();

const { t } = useI18n();

const { uploadAndCreateUserDocument, isPending: isUploadingUserDocument } =
  useUploadAndCreateUserDocument();

const { handleSubmit } = useForm({
  validationSchema: toTypedSchema(
    yup.object({
      name: yup.string().required(),
      type: yup.string().oneOf(Object.values(UserDocumentType)).required(),
      file: yup.mixed().required(),
    }),
  ),
});

const saveDocument = handleSubmit(async (values) => {
  await uploadAndCreateUserDocument(props.user.id, values.file as File, values.type, values.name);
  emit('complete');
});

const documentTypes = computed(() =>
  Object.entries(UserDocumentType).map(([key, value]) => ({
    label: t(`userDocumentTypes.${key}`),
    value,
  })),
);
</script>

<i18n lang="json">
{
  "en": {
    "name": "Name",
    "documentType": "Document Type",
    "upload": "Upload",
    "saveDocument": "Save Document"
  },
  "de": {
    "name": "Name",
    "documentType": "Dokumententyp",
    "upload": "Hochladen",
    "saveDocument": "Dokument Speichern"
  },
  "es": {
    "name": "Nombre",
    "documentType": "Tipo de documento",
    "upload": "Subir",
    "saveDocument": "Guardar documento"
  }
}
</i18n>
